import React, { useState, useCallback, useEffect } from "react";
import { Modal, Button, Form, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useDropzone } from "react-dropzone";
import { GrFormAdd, GrClose } from "react-icons/gr";
import { RiCloseFill } from "react-icons/ri";
import axios from "axios";
import EngagementModal from "./EngagementModal";
import IconUpload from '../../images/dashboard/icon-upload.png';
import IconWord from '../../images/dashboard/icon-word.png';
import IconPdf from '../../images/dashboard/icon-pdf.png';
import IocnSuccess from "../../images/success-icon.png";
import IconInfo from "../../images/dashboard/icon-info.png";
import apiConfig from "../credentials/apiConfig";
import './index.scss';
// import industryOptions from "../../constants/industryOptions";
const AddClientModal = ({ isOpen, toggle, onToggle }) => {
  const [file, setFile] = useState(null);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [statesError, setStatesError] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [industries, setIndustries] = useState([]);


  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(`${apiConfig.apiBaseURL}/getAllIndustries`, {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/json"
          }
        });
  
        if (response.data && response.data.success) {
          const result = response.data.data.map(item => item.name);
          setIndustries(result);
        } else {
          setIndustries([]);
        }
      } catch (error) {
        console.error("Failed to fetch industries:", error);
        setIndustries([]);
      }
    };
  
    fetchIndustries();
  }, []);
  

  useEffect(() => {
    const fetchStates = async () => {
      setStatesLoading(true);
      setStatesError(null);
      try {
        const response = await axios.get(
          `${apiConfig.apiBaseURL}/getAllStates`,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Content-Type": "application/json",
            },
          }
        );
        if (response.data && response.data.success) {
          const usStates = response.data.data.filter(
            (state) => state.countryId === 231 && !state.deleted
          );
          usStates.sort((a, b) => a.name.localeCompare(b.name));
          setStates(usStates);
        } else {
          setStatesError(response.data.error);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        setStatesError("Error fetching states: " + (error.message || "Unknown error"));
      } finally {
        setStatesLoading(false);
      }
    };

    fetchStates();
  }, []);

  // Initialize formik with contactInfo as the single source of truth.
  const formik = useFormik({
    initialValues: {
      clientName: "",
      industry: "",
      description: "",
      file: null,
      // Notice that each contact now has a "removed" flag
      contactInfo: [{ name: "", role: "", email: "", phone: "", primary: false, removed: false }],
      address1: "",
      address2: "",
      city: "",
      state: "",
      stateCode: "",
      zip: ""
    },
    validationSchema: Yup.object({
      clientName: Yup.string().required("Client Name is required"),
      industry: Yup.string().required("Industry is required"),
      description: Yup.string()
        .test(
          "description-or-file",
          "Either Description or Upload Document is required.",
          function (value) {
            // If no file is uploaded, description must be present
            return !!value || !!this.parent.file;
          }
        ),
      phone: Yup.string()
        .transform(value => value ? value.replace(/\D/g, "") : '')
        .test(
          "phone",
          "Phone number must be 10 digits",
          value => !value || value.length === 10
        ),
      file: Yup.mixed()
        .nullable()
        .test(
          "file-or-description",
          "Either Upload Document or Description is required.",
          function (value) {
            // If no description is provided, file must be present
            return !!value || !!this.parent.description;
          }
        )
        .test("fileType", "Only PDF, DOC, and DOCX files are allowed.", (value) => {
          if (!value) return true;
          const allowedTypes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ];
          return allowedTypes.includes(value.type);
        })
        .test("fileSize", "File size must be 25MB or less.", (value) => {
          if (!value) return true;
          return value.size <= 25 * 1024 * 1024;
        }),
      contactInfo: Yup.array().of(
        Yup.object().shape({
          email: Yup.string()
            .test('valid-email', 'Invalid email format', function (value) {
              if (value) {
                return Yup.string().email().isValidSync(value);
              }
              return true;
            })
        })
      ),
      address1: Yup.string().required("Address 1 is required"),
      city: Yup.string().required("City is required"),
      state: Yup.string().required("State is required"),
      zip: Yup.string()
        .matches(/^\d{5}$/, "ZIP Code must be 5 digits")
        .required("ZIP Code is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      console.log("Submitting form:", values);
      setLoading(true);
      setButtonDisabled(true);

      // If needed, filter out removed contacts before sending data
      const filteredContacts = values.contactInfo.filter(contact => !contact.removed);

      const formData = new FormData();

      const clientData = {
        clientName: values.clientName,
        industry: values.industry,
        companySummary: { description: values.description || "" },
        contactInformation: filteredContacts,
        address1: values.address1 || "",
        address2: values.address2 || "",
        city: values.city || "",
        state: values.state,  // Use the state name instead of ID
        stateCode: values.stateCode,  // Use the state code
        zipCode: values.zip,
      };

      formData.append("clientName", clientData.clientName);
      formData.append("industry", clientData.industry);
      formData.append("description", clientData.companySummary.description);
      formData.append("contactInformation", JSON.stringify(clientData.contactInformation));
      formData.append("address1", clientData.address1);
      formData.append("address2", clientData.address2);
      formData.append("city", clientData.city);
      formData.append("state", clientData.state);
      formData.append("stateCode", clientData.stateCode);
      formData.append("zipCode", clientData.zipCode);

      if (file) {
        formData.append("file", file);
      }
      const authToken = localStorage.getItem("authToken");

      axios.post(
        `${apiConfig.apiBaseURL}/createClient`,
        formData,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${authToken}`
          }
        }
      )
        .then(response => {
          if (response?.data?.success) {
          Swal.fire({
  allowOutsideClick: false,
  imageUrl: IocnSuccess,
  title: "Client Added Successfully",
  text: `${values.clientName} has been added to your clients`,
  confirmButtonText: "Create New Engagement",
  showCloseButton: true,
  customClass: {
    container: "custom-swal-container",
    popup: "custom-swal-popup",
  },
}).then((result) => {
  if (result.isConfirmed) {
    // User clicked "Create New Engagement"
    resetForm();
    toggle(); // Close AddClientModal
    setShowEngagementModal(true); // Open EngagementModal
  } else {
    // User clicked close button (X) or pressed Escape
    resetForm();
    toggle(); // Just close AddClientModal, don't open EngagementModal
  }
});
          } else {
            toast.error(response?.data?.message || "Failed to create client");
          }
        })
        .catch(error => {
          toast.error(error.response?.data?.message || "Error creating client");
        })
        .finally(() => {
          setLoading(false);
          setButtonDisabled(false);
        });
    }
  });

  // Handle file drop via dropzone
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const droppedFile = acceptedFiles[0];
        setFile(droppedFile);
        formik.setFieldValue('file', droppedFile);
      }
    },
    [formik]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
  });

  const handleRemoveFile = () => {
    setFile(null);
    formik.setFieldValue('file', null);
  };

  // Update the contactInfo array in formik when adding a new contact
  const addContact = () => {
    const newContact = { name: "", role: "", email: "", phone: "", primary: false, removed: false };
    formik.setFieldValue("contactInfo", [...formik.values.contactInfo, newContact]);
  };

  // Instead of removing the contact completely, mark it as removed (if not primary)
  const removeContact = (contactIndex) => {
    // Prevent removing the primary contact (index 0)
    if (contactIndex === 0) return;

    const updatedContacts = formik.values.contactInfo.filter((_, index) => index !== contactIndex);
    formik.setFieldValue("contactInfo", updatedContacts);
  };

  const handleShowModal = () => setShowEngagementModal(true);
  const handleCloseModal = () => setShowEngagementModal(false);
  const handleToggle = (value) => {
    onToggle(value)
    setIsChecked(value);
  };

  const handleCancel = () => {
  Swal.fire({
    title: "Are you sure?",
    text: "Are you sure you don't want to create the new client?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#073E72",
    cancelButtonColor: "#fff",
    confirmButtonText: "Yes",
    cancelButtonText: "No",
    customClass: {
      popup: "custom-swal-popup",
    },
  }).then((result) => {
    if (result.isConfirmed) {
      toggle(); // Close the AddClientModal
    }
  });
};


  return (
    <>
      <Modal
        show={isOpen}
        onHide={handleCancel}
        size="lg"
        backdrop="static"
        keyboard={false}
        className="credential-page"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-dark" style={{ fontSize: "24px" }}>
            Add Client
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Body>
            <h3 className="fw-bold text-dark" style={{ fontSize: "20px" }}>
              Basic Information
            </h3>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Client Name*</Form.Label>
                  <Form.Control
                    {...formik.getFieldProps("clientName")}
                    placeholder="Enter client's name"
                    className="p-3"
                  />
                  {formik.touched.clientName && formik.errors.clientName && (
                    <div className="text-danger">{formik.errors.clientName}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Industry*</Form.Label>
                  <Form.Select
  {...formik.getFieldProps("industry")}
  className="p-3"
>
  <option value="">Select Industry</option>
  {industries.map((industry, index) => (
    <option key={index} value={industry}>
      {industry}
    </option>
  ))}
</Form.Select>

                  {formik.touched.industry && formik.errors.industry && (
                    <div className="text-danger">{formik.errors.industry}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <hr className="my-4" />
            <h3 className="fw-bold text-dark" style={{ fontSize: "20px" }}>
              Company Summary*
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="button-tooltip-2">
                    Copy and paste or upload company summary paragraph(s) from prior year financials.
                  </Tooltip>
                }
              >
                <img src={IconInfo} alt="info" style={{ marginLeft: "5px", cursor: "pointer" }} />
              </OverlayTrigger>
            </h3>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Description*</Form.Label>
                  <Form.Control
                    as="textarea"
                    {...formik.getFieldProps("description")}
                    placeholder="Description Here"
                    style={{ backgroundColor: "#E7EFF8", borderRadius: "8px" }}
                    rows={4}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <div className="text-danger">{formik.errors.description}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Upload document</Form.Label>
                  <div
                    {...getRootProps({
                      className: "dropzone p-3 text-center d-flex align-items-center justify-content-between",
                    })}
                    style={{ backgroundColor: "#E7EFF8", borderRadius: "8px" }}
                  >
                    <input {...getInputProps()} />
                    {file ? (
                      <div className="d-flex flex-column w-100">
                        <div className="d-flex">
                          <img
                            src={file.name.endsWith(".pdf") ? IconPdf : IconWord}
                            className="me-2 h-100 my-auto"
                            alt="icon"
                          />
                          <div className="fw-bold text-dark mb-0" style={{ textAlign: "left" }}>
                            {file.name}
                            <span className="w-100 d-block fw-normal text-light">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <div style={{ textAlign: 'end' }}>
                            <RiCloseFill
                              size={25}
                              onClick={handleRemoveFile}
                              className="ms-2 bg-transparent border-0"
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center w-100">
                        <img src={IconUpload} alt="icon" />
                        <p className="mb-0 text-light fw-bold" style={{ fontSize: "14px" }}>
                          Drag & Drop or <span className="text-primary">Choose file</span> to upload
                        </p>
                        <p className="mb-0 text-light">Word or Pdf</p>
                      </div>
                    )}
                  </div>
                  {formik.touched.file && formik.errors.file && (
                    <div className="text-danger mt-2">{formik.errors.file}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <hr className="my-4" />
            <h3 className="fw-bold text-dark" style={{ fontSize: "20px" }}>
              Contact Information
            </h3>
            {
              // Render only contacts that are not marked as removed
              formik.values.contactInfo.map((contact, index) => {
                if (contact.removed) return null;
                return (
                  <Row
                    key={index}
                    className={`align-items-center ${index !== 0 ? "client-add-custom rounded p-4 mb-2 position-relative" : ""}`}
                  >
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control
                          placeholder="Contact Name"
                          className="p-3"
                          name={`contactInfo[${index}].name`}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={contact.name}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Role</Form.Label>
                        <Form.Control
                          placeholder="Role"
                          className="p-3"
                          name={`contactInfo[${index}].role`}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={contact.role}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Email"
                          className="p-3"
                          name={`contactInfo[${index}].email`}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={contact.email}
                        />
                        {formik.touched.contactInfo &&
                          formik.touched.contactInfo[index] &&
                          formik.touched.contactInfo[index].email &&
                          formik.errors.contactInfo &&
                          formik.errors.contactInfo[index] &&
                          formik.errors.contactInfo[index].email && (
                            <div className="text-danger">
                              {formik.errors.contactInfo[index].email}
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="(XXX) XXX-XXXX"
                          className="p-3"
                          name={`contactInfo[${index}].phone`}
                          value={contact.phone}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, "");
                            if (value.length > 10) value = value.slice(0, 10);
                            if (value.length > 6) {
                              value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
                            } else if (value.length > 3) {
                              value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
                            } else if (value.length > 0) {
                              value = `(${value}`;
                            }
                            formik.setFieldValue(`contactInfo[${index}].phone`, value);
                          }}
                          onBlur={formik.handleBlur}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} className="mb-3 d-flex align-items-center">
                      {index !== 0 && (
                        <>
                          <Form.Check
                            label="Primary"
                            className="pt-1"
                            name={`contactInfo[${index}].primary`}
                            onChange={(e) => {
                              const updatedValue = e.target.checked;
                              formik.setFieldValue(`contactInfo[${index}].primary`, updatedValue);
                            }}
                            checked={contact.primary || false}
                          />
                          <span
                            style={{ position: "absolute", top: 12, right: 12, cursor: "pointer" }}
                            onClick={() => removeContact(index)}
                          >
                            <GrClose className="text-dark" size={20} />
                          </span>
                        </>
                      )}
                    </Col>
                  </Row>
                );
              })
            }
            {formik.values.contactInfo && formik.values.contactInfo.length <= 1 && <Button
              variant="link"
              className="text-primary text-decoration-none fw-bold"
              onClick={addContact}
            >
              <GrFormAdd /> Add Another Contact
            </Button>}
            <hr className="my-4" />
            <h3 className="fw-bold text-dark" style={{ fontSize: "20px" }}>
              Address
            </h3>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Address 1*</Form.Label>
                  <Form.Control
                    placeholder="Address 1"
                    {...formik.getFieldProps("address1")}
                    className="p-3"
                  />
                  {formik.touched.address1 && formik.errors.address1 && (
                    <div className="text-danger">{formik.errors.address1}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Address 2</Form.Label>
                  <Form.Control
                    placeholder="Address 2"
                    {...formik.getFieldProps("address2")}
                    className="p-3"
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label>City*</Form.Label>
                  <Form.Control
                    placeholder="City"
                    {...formik.getFieldProps("city")}
                    className="p-3"
                  />
                  {formik.touched.city && formik.errors.city && (
                    <div className="text-danger">{formik.errors.city}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>State*</Form.Label>
                  <Form.Select
                    name="state"
                    onChange={(e) => {
                      const selectedState = states.find(
                        state => state.code === e.target.value
                      );
                      formik.setFieldValue("stateCode", e.target.value);
                      formik.setFieldValue("state", selectedState ? selectedState.name : "");
                    }}
                    value={formik.values.stateCode}
                    isInvalid={formik.touched.state && formik.errors.state}
                    disabled={statesLoading}
                    className="p-3"
                  >
                    <option value="">Select State</option>
                    {statesLoading ? (
                      <option>Loading states...</option>
                    ) : (
                      states.map(state => (
                        <option key={state._id} value={state.code}>
                          {state.name}
                        </option>
                      ))
                    )}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.state}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label>ZIP Code*</Form.Label>
                  <Form.Control
                    placeholder="ZIP Code"
                    {...formik.getFieldProps("zip")}
                    className="p-3"
                  />
                  {formik.touched.zip && formik.errors.zip && (
                    <div className="text-danger">{formik.errors.zip}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-primary" className="px-5 py-3" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="text-white font-16 px-5 py-3"
              type="submit"
              disabled={loading || buttonDisabled}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <EngagementModal
        show={showEngagementModal}
        handleClose={handleCloseModal}
        clientId={clientId}
        onToggle={handleToggle}
      />

      {/* <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} /> */}
    </>
  );
};

export default AddClientModal;